// App root: i18n, DB, auth, routing, cookie consent
import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { I18nProvider } from '@/i18n';
import { initDatabase } from '@/lib/db';
import { useSettingsStore } from '@/store/settingsStore';
import { useAuthStore } from '@/store/authStore';
import { useNotesStore } from '@/store/notesStore';
import { usePersonalTasksStore } from '@/store/personalTasksStore';
import { useProfitTasksStore } from '@/store/profitTasksStore';
import { useRemindersStore } from '@/store/remindersStore';
import { Layout } from '@/components/layout/Layout';
import { NotesPage } from '@/pages/Notes';
import { PersonalTasksPage } from '@/pages/PersonalTasks';
import { ProfitTasksPage } from '@/pages/ProfitTasks';
import { RemindersPage } from '@/pages/Reminders';
import { SettingsPage } from '@/pages/Settings';
import { AuthPage } from '@/pages/Auth';
import { ConfirmHost } from '@/components/ui/Confirm';
import { CookieConsentBanner } from '@/components/CookieConsent';
import { useToastStore } from '@/hooks/useToast';
import { Spinner, Toast } from '@/components/ui/Misc';
import { CopyrightPage } from './pages/CopyrightPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { TermsPage } from './pages/TermsPage';

export default function App() {
  return (
    <I18nProvider>
      <AppInner />
    </I18nProvider>
  );
}

function AppInner() {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { load: loadSettings, applyTheme } = useSettingsStore();
  const { init: initAuth } = useAuthStore();
  const notesStore = useNotesStore();
  const personalStore = usePersonalTasksStore();
  const profitStore = useProfitTasksStore();
  const remindersStore = useRemindersStore();
  const toastState = useToastStore();

  useEffect(() => {
    (async () => {
      try {
        await initDatabase();
        await loadSettings();
        const s = useSettingsStore.getState().settings;
        applyTheme(s.theme);
        await Promise.all([
          notesStore.load(),
          personalStore.load(),
          profitStore.load(),
          remindersStore.load(),
        ]);
        await initAuth();
        setReady(true);
      } catch (e: any) {
        console.error('[App] init error', e);
        setError(e?.message ?? 'Errore inizializzazione database');
        setReady(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listener per cambio tema di sistema
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      const s = useSettingsStore.getState().settings;
      if (s.theme === 'system') applyTheme('system');
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [applyTheme]);

  if (!ready) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 gap-3">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-500 to-purple-500 flex items-center justify-center text-white text-2xl font-bold shadow-lg shadow-brand-500/30 animate-scale-in">
          PN
        </div>
        <Spinner size={24} />
        <p className="text-xs text-slate-400">Pira Notes</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 gap-3 p-6 text-center">
        <div className="w-14 h-14 rounded-2xl bg-rose-100 dark:bg-rose-900/40 flex items-center justify-center text-rose-600 text-2xl">!</div>
        <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">Errore di inizializzazione</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm">{error}</p>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/" element={<Navigate to="/notes" replace />} />
        <Route path="/notes" element={<Layout><NotesPage /></Layout>} />
        <Route path="/personal" element={<Layout kanban><PersonalTasksPage /></Layout>} />
        <Route path="/profit" element={<Layout kanban><ProfitTasksPage /></Layout>} />
        <Route path="/reminders" element={<Layout><RemindersPage /></Layout>} />
        <Route path="/settings" element={<Layout><SettingsPage /></Layout>} />
        <Route path="/privacy" element={<Layout><PrivacyPage /></Layout>} />
        <Route path="/terms" element={<Layout><TermsPage /></Layout>} />
        <Route path="/copyright" element={<Layout><CopyrightPage /></Layout>} />
        <Route path="*" element={<Navigate to="/notes" replace />} />
      </Routes>
      <ConfirmHost />
      <CookieConsentBanner />
      {toastState.visible && (
        <Toast message={toastState.message} type={toastState.type} />
      )}
    </BrowserRouter>
  );
}

// Handler OAuth callback: Supabase reindirizza qui dopo login Google
function AuthCallback() {
  const navigate = useNavigate();
  useEffect(() => {
    const t = setTimeout(() => navigate('/notes'), 800);
    return () => clearTimeout(t);
  }, [navigate]);
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 gap-3">
      <Spinner size={28} />
      <p className="text-sm text-slate-500">Pira Notes</p>
    </div>
  );
}
